import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { createAppTheme, type ThemeBranding } from './theme';

type Mode = 'light' | 'dark';
type Density = 'COMFORTABLE' | 'COMPACT';

interface ThemeModeContextValue {
  mode: Mode;
  toggle: () => void;
  /** The density in force, whether it came from the person or the tenant. */
  density: Density;
  /** Switch density for THIS person, on this device. */
  toggleDensity: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);
const STORAGE_KEY = 'oip.theme';
/*
 * Density is a personal preference, stored the same way as colour mode.
 *
 * Both are about COMFORT rather than brand: whether rows should be tight depends
 * on eyesight, screen size and viewing distance, not on what the organisation's
 * product should look like. Compact also shrinks touch targets, which is a real
 * accessibility problem for somebody who cannot currently solve it.
 *
 * The tenant sets a default; a person who chooses keeps their choice.
 */
const DENSITY_KEY = 'oip.density';

function storedMode(): Mode | null {
  const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
  return saved === 'light' || saved === 'dark' ? saved : null;
}

function systemMode(): Mode {
  return typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: light)').matches
    ? 'light'
    : 'dark';
}

function initialMode(): Mode {
  return storedMode() ?? systemMode();
}

function storedDensity(): Density | null {
  const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(DENSITY_KEY) : null;
  return saved === 'COMFORTABLE' || saved === 'COMPACT' ? saved : null;
}

/**
 * Light/dark mode and the MUI theme.
 *
 * Takes branding as a PROP rather than fetching it. This provider is mounted by
 * component tests and stories with nothing else around it, and making a
 * presentation provider depend on a QueryClient and an auth context would be a
 * coupling those callers pay for and never use. `TenantThemeProvider` does the
 * fetching and hands the result down.
 */
export function ThemeModeProvider({
  children,
  branding,
  defaultColourMode,
}: {
  children: ReactNode;
  branding?: ThemeBranding;
  /** The tenant's preferred STARTING mode, applied only if nobody chose one. */
  defaultColourMode?: 'SYSTEM' | 'LIGHT' | 'DARK' | null;
}) {
  const [mode, setMode] = useState<Mode>(initialMode);
  // `null` means "follow the tenant" — the person has not chosen.
  const [ownDensity, setOwnDensity] = useState<Density | null>(storedDensity);

  /*
   * The tenant default applies only when the PERSON has not chosen.
   *
   * A default, not a lock (design D5). Forcing a mode on somebody working a night
   * shift would be branding taking precedence over their eyes — so a stored
   * choice always wins, and this runs once when branding arrives.
   */
  useEffect(() => {
    if (!defaultColourMode || defaultColourMode === 'SYSTEM') return;
    if (storedMode() !== null) return;
    setMode(defaultColourMode === 'LIGHT' ? 'light' : 'dark');
  }, [defaultColourMode]);
  const toggle = useCallback(() => {
    setMode((m) => {
      const next = m === 'dark' ? 'light' : 'dark';
      localStorage.setItem(STORAGE_KEY, next);
      return next;
    });
  }, []);
  // The person's choice wins; otherwise the tenant's default; otherwise ours.
  const density: Density = ownDensity ?? branding?.density ?? 'COMFORTABLE';

  const toggleDensity = useCallback(() => {
    setOwnDensity((current) => {
      const base = current ?? branding?.density ?? 'COMFORTABLE';
      const next: Density = base === 'COMPACT' ? 'COMFORTABLE' : 'COMPACT';
      localStorage.setItem(DENSITY_KEY, next);
      return next;
    });
  }, [branding?.density]);

  const theme = useMemo(
    () => createAppTheme(mode, { ...branding, density }),
    [mode, branding, density],
  );
  const value = useMemo(
    () => ({ mode, toggle, density, toggleDensity }),
    [mode, toggle, density, toggleDensity],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode(): ThemeModeContextValue {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) throw new Error('useThemeMode must be used within ThemeModeProvider');
  return ctx;
}
